import { useMemo, useState } from 'react';
import { MeasurementShiftDelay, MeasurementShiftDelayItem } from '../../../../shared/types/measurementShiftDelay.ts';
import { Dialog } from '../../../components/dialog-portal/dialog.tsx';
import {
    formatDateTime,
    formatMinutes,
    formatShiftEnd,
    formatShiftName,
} from './measurementShiftDelayFormats.ts';

interface Props {
    shiftDelay: MeasurementShiftDelay;
    onClose: () => void;
}

type SortField = 'equipment' | 'product' | 'targetTime' | 'delayMinutes' | 'resolvedAt';
type SortDirection = 'asc' | 'desc';

export function MeasurementShiftDelayDialog({ shiftDelay, onClose }: Props) {
    const [sort, setSort] = useState<{ field: SortField; direction: SortDirection }>({
        field: 'delayMinutes',
        direction: 'desc',
    });
    const sortedItems = useMemo(
        () => [...shiftDelay.items].sort((a, b) => compareItems(a, b, sort.field, sort.direction)),
        [shiftDelay.items, sort],
    );

    function toggleSort(field: SortField) {
        setSort((previous) => ({
            field,
            direction: previous.field === field
                ? previous.direction === 'asc' ? 'desc' : 'asc'
                : field === 'equipment' || field === 'product' ? 'asc' : 'desc',
        }));
    }

    return (
        <Dialog title={`Unresolved Measurements - ${formatShiftEnd(shiftDelay.shiftEnd)}`} onClose={onClose} large>
            <div className='measurement-shift-delay-dialog'>
                <p style={{ marginTop: 0 }}>
                    <strong>{formatShiftName(shiftDelay.shiftSlot, shiftDelay.shiftCode)}</strong> ended with{' '}
                    <strong>{shiftDelay.items.length}</strong> unresolved measurement
                    {shiftDelay.items.length === 1 ? '' : 's'} due during that shift, totaling{' '}
                    <strong>{formatMinutes(shiftDelay.totalDelayMinutes)}</strong>.
                </p>
                <div className='measurement-shift-delay-note'>
                    <details>
                        <summary>About this data and why some delays may be missing</summary>
                        <div>
                            <p>
                                Measurements completed after the shift ended are included, and measurements that are still open now are
                                shown as <strong>Still open</strong>. Their delay is calculated only up to the end of this shift.
                            </p>
                            <p>
                                No snapshot is stored at shift end. A delay can therefore be reconstructed only while its task is still
                                open, or after a completed measurement creates a delay record.
                            </p>
                            <p>
                                An unfinished target may disappear before either happens when:
                            </p>
                            <ul>
                                <li>The product or production batch changes.</li>
                                <li>The configured measurement interval changes.</li>
                                <li>An offline target is invalidated when the machine returns online.</li>
                                <li>A later measurement makes the older target invalid.</li>
                                <li>The machine is disabled or removed from the active equipment list, removing its live task.</li>
                            </ul>
                            <p>
                                In those cases there may be no completion record or current live task from which to recover the original
                                delay, so it will not be represented in this report.
                            </p>
                        </div>
                    </details>
                </div>
                {shiftDelay.items.length === 0 ? (
                    <p>No unresolved measurements were found for this shift.</p>
                ) : (
                    <table className='measurement-shift-delay-table'>
                        <thead>
                            <tr>
                                <SortableHeader label='Equipment' field='equipment' sort={sort} onSort={toggleSort} />
                                <SortableHeader label='Product' field='product' sort={sort} onSort={toggleSort} />
                                <SortableHeader label='Due' field='targetTime' sort={sort} onSort={toggleSort} />
                                <SortableHeader label='Delay at shift end' field='delayMinutes' sort={sort} onSort={toggleSort} />
                                <SortableHeader label='Resolved' field='resolvedAt' sort={sort} onSort={toggleSort} />
                            </tr>
                        </thead>
                        <tbody>
                            {sortedItems.map((item) => (
                                <tr key={`${item.equipment}-${item.targetTime.toISOString()}`}>
                                    <td className='measurement-shift-delay-table__equipment'>{item.equipment}</td>
                                    <td>{item.product ?? 'n/a'}</td>
                                    <td>{formatDateTime(item.targetTime)}</td>
                                    <td className='measurement-shift-delay-table__delay'>
                                        {formatMinutes(item.delayMinutes)}
                                    </td>
                                    <td>
                                        {item.resolvedAt ? formatDateTime(item.resolvedAt) : 'Still open'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </Dialog>
    );
}

interface SortableHeaderProps {
    label: string;
    field: SortField;
    sort: { field: SortField; direction: SortDirection };
    onSort: (field: SortField) => void;
}

function SortableHeader({ label, field, sort, onSort }: SortableHeaderProps) {
    const isSorted = sort.field === field;

    return (
        <th aria-sort={isSorted ? sort.direction === 'asc' ? 'ascending' : 'descending' : 'none'}>
            <button
                type='button'
                className='measurement-shift-delay-table__sort'
                onClick={() => onSort(field)}
            >
                <span>{label}</span>
                <span
                    className={[
                        'measurement-shift-delay-table__sort-indicator',
                        isSorted
                            ? `measurement-shift-delay-table__sort-indicator--${sort.direction}`
                            : 'measurement-shift-delay-table__sort-indicator--hidden',
                    ].join(' ')}
                    aria-hidden='true'
                />
            </button>
        </th>
    );
}

function compareItems(
    a: MeasurementShiftDelayItem,
    b: MeasurementShiftDelayItem,
    field: SortField,
    direction: SortDirection,
): number {
    let comparison: number;
    if (field === 'equipment') {
        comparison = a.equipment.localeCompare(b.equipment);
    } else if (field === 'product') {
        comparison = (a.product ?? '').localeCompare(b.product ?? '');
    } else if (field === 'targetTime') {
        comparison = a.targetTime.getTime() - b.targetTime.getTime();
    } else if (field === 'delayMinutes') {
        comparison = a.delayMinutes - b.delayMinutes;
    } else {
        comparison = compareNullableDates(a.resolvedAt, b.resolvedAt);
    }

    if (comparison === 0) comparison = a.equipment.localeCompare(b.equipment);
    return direction === 'asc' ? comparison : -comparison;
}

function compareNullableDates(a: Date | null, b: Date | null): number {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    return a.getTime() - b.getTime();
}
